import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox/Checkbox";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea/Textarea";
import { diagnosisItems as cdmDiagnosisItems } from "features/cdm/cdm/constants/diagnosisItems";
import { otherMajorDiagnosisItems } from "features/cdm/cdm/constants/otherMajorDiagnosisItems";
import {
  getReferralOptions,
  MANAGEMENT_TOPICS,
  MAX_TAKE_HOME_MESSAGES,
  SELF_MANAGEMENT_PLAN_OPTIONS,
} from "features/cdm/constants/carePlanOptions";
import { AGREED_WRITTEN_CARE_PLAN_OPTIONS } from "features/cdm/constants/formOptions";
import { diagnosisItems } from "features/cdm/pp/constants/diagnosisItems";
import {
  defaultNextReviewDate,
  toCarePlanIsoDate,
} from "features/cdm/utils/carePlanDate";
import { ensureAbsoluteUrl } from "features/cdm/utils/ensureAbsoluteUrl";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ItemFormWrapper } from "../ItemFormWrapper/ItemFormWrapper";
import style from "./CarePlanForm.module.css";
import { CarePlanAutoBlocks } from "./components/CarePlanAutoBlocks";
import { CARE_PLAN_INFO, isDiabetesInfoLink } from "./constants";

// When REACT_APP_SHOW_ALL_CAREPLAN_FIELDS === "true" the full legacy field set
// is shown. Absent (the default), the Socrates/legacy input boxes struck out on
// the HSE GP-screen samples are hidden (KIN-1370). Reversible via the flag until
// the removal is formally signed off.
const SHOW_ALL_CAREPLAN_FIELDS =
  process.env.REACT_APP_SHOW_ALL_CAREPLAN_FIELDS === "true";

const carePlanSchema = z
  .object({
    carerName: z.string().optional(),
    carerConsentToDisclose: z.boolean().optional(),
    otherMajorDiagnoses: z.string().optional(),
    medicationConcerns: z.string().optional(),
    medicationIssuesDiscussed: z.string().optional(),
    healthConcerns: z.string().optional(),
    healthImprovements: z.string().optional(),
    thingsThatMatter: z.string().optional(),
    healthIssuesDiscussed: z.string().optional(),
    conditionManagement: z.record(z.string(), z.string().optional()).optional(),
    previousGoals: z
      .array(
        z.object({
          description: z.string().optional(),
          progress: z.string().optional(),
        })
      )
      // When the last review's goals are carried forward, the BE requires progress
      // on at least one of them. Mirror that here so it's caught before submit and
      // the error lands on the first previous goal's progress input.
      .superRefine((goals, ctx) => {
        // The previous-goals block is hidden when the flag is off, so there is no
        // input to satisfy this rule — skip it (the goals aren't carried forward
        // or submitted in that mode either).
        if (!SHOW_ALL_CAREPLAN_FIELDS) return;
        if (!goals?.length) return;

        const hasProgress = (goals ?? []).some((goal) => goal.progress?.trim());

        if (!hasProgress) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Progress must be recorded on at least one goal from the last review",
            path: [0, "progress"],
          });
        }
      })
      .optional(),
    currentGoals: z
      .array(
        z.object({
          description: z.string().optional(),
          plan: z.string().optional(),
        })
      )
      // Attach the error to the first goal's description so the input gets the
      // error border + inline message like every other field (not a root-only
      // error that leaves the input unstyled).
      .superRefine((goals, ctx) => {
        const hasDescription = (goals ?? []).some((goal) =>
          goal.description?.trim()
        );

        if (!hasDescription) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "At least one goal for this review must have a description",
            path: [0, "description"],
          });
        }
      }),
    anticipatoryCarePlan: z.string().optional(),
    printSelfManagementAppendix: z.array(z.string()).optional(),
    referrals: z
      .array(
        z.object({
          service: z.string().optional(),
          detail: z.string().optional(),
        })
      )
      .optional(),
    takeHomeMessages: z
      .array(z.object({ value: z.string().optional() }))
      .optional(),
    nextReviewDate: z.string().optional(),
    sourcesFreeText: z.string().optional(),
    agreedWrittenCarePlan: z
      .string()
      .min(1, "Agreed written care plan is required"),
  })
  .refine(
    (values) => !values.carerConsentToDisclose || !!values.carerName?.trim(),
    {
      path: ["carerName"],
      message: "Carer name is required when consent to disclose is given",
    }
  );

const buildPayload = (values) => {
  const conditionManagement = MANAGEMENT_TOPICS.map(({ topic }) => ({
    topic,
    detail: values.conditionManagement?.[topic]?.trim() || undefined,
  })).filter((item) => item.detail);

  const previousGoals = (values.previousGoals ?? [])
    .filter((goal) => goal.description?.trim())
    .map((goal) => ({
      description: goal.description.trim(),
      progress: goal.progress?.trim() || undefined,
    }));

  const currentGoals = (values.currentGoals ?? [])
    .filter((goal) => goal.description?.trim())
    .map((goal) => ({
      description: goal.description.trim(),
      plan: goal.plan?.trim() || undefined,
    }));

  const referrals = (values.referrals ?? [])
    .filter((referral) => referral.service)
    .map((referral) => ({
      service: referral.service,
      detail: referral.detail?.trim() || undefined,
    }));

  const takeHomeMessages = (values.takeHomeMessages ?? [])
    .map((message) => message.value?.trim())
    .filter(Boolean);

  const trimmed = (value) => value?.trim() || undefined;

  return {
    carerName: trimmed(values.carerName),
    carerConsentToDisclose: values.carerConsentToDisclose || undefined,
    otherMajorDiagnoses: trimmed(values.otherMajorDiagnoses),
    medicationConcerns: trimmed(values.medicationConcerns),
    medicationIssuesDiscussed: trimmed(values.medicationIssuesDiscussed),
    healthConcerns: trimmed(values.healthConcerns),
    healthImprovements: trimmed(values.healthImprovements),
    thingsThatMatter: trimmed(values.thingsThatMatter),
    healthIssuesDiscussed: trimmed(values.healthIssuesDiscussed),
    conditionManagement: conditionManagement.length
      ? conditionManagement
      : undefined,
    previousGoals: previousGoals.length ? previousGoals : undefined,
    currentGoals: currentGoals.length ? currentGoals : undefined,
    anticipatoryCarePlan: trimmed(values.anticipatoryCarePlan),
    printSelfManagementAppendix: values.printSelfManagementAppendix?.length
      ? values.printSelfManagementAppendix
      : undefined,
    referrals: referrals.length ? referrals : undefined,
    takeHomeMessages: takeHomeMessages.length ? takeHomeMessages : undefined,
    nextReviewDate: trimmed(values.nextReviewDate),
    sourcesFreeText: trimmed(values.sourcesFreeText),
  };
};

const toConditionManagementMap = (conditionManagement) =>
  (conditionManagement ?? []).reduce((acc, item) => {
    acc[item.topic] = item.detail ?? "";
    return acc;
  }, {});

// Expand the first-step "Other Major Diagnoses" (structured { diagnoses, otherText })
// into a display list. Selected diagnoses become one line each; the free-text entry
// is shown as a single "Other" line together with its comment.
export const composeOtherMajorDiagnosesList = (otherMajorDiagnoses) => {
  const names = (otherMajorDiagnoses?.diagnoses ?? []).map(
    (id) => otherMajorDiagnosisItems.find((item) => item.id === id)?.name ?? id
  );
  const otherText = otherMajorDiagnoses?.otherText?.trim();

  return [...names, otherText ? `Other: ${otherText}` : null].filter(Boolean);
};

// PP only — the "Risk factors & reason for enrolment in Prevention Programme"
// section (HSE PP GP Screen §4B) replaces CDM's "Other Major Diagnoses". Map the
// PP registration reason enum ids to their readable labels (same source the PP
// preview/registration dialog use).
export const composeRegistrationReasonsList = (registrationReasons) =>
  (registrationReasons ?? []).map(
    (id) => diagnosisItems.find((item) => item.id === id)?.name ?? id
  );

// CDM "Medical Conditions" (HSE CDM GP Screen §4A.3): the patient's registered
// CDM disease list, auto-populated from the review diagnoses. Maps the diagnosis
// enum ids to readable labels via the CDM diagnosis items. Distinct from the
// free-text "Other Major Diagnoses" field.
export const composeMedicalConditionsList = (registrationReasons) =>
  (registrationReasons ?? []).map(
    (id) => cdmDiagnosisItems.find((item) => item.id === id)?.name ?? id
  );

const buildPreviousGoals = (carePlanView, existing) => {
  if (existing?.length) return existing;
  const carryForward = carePlanView?.carryForwardPreviousGoals ?? [];
  return carryForward.map((goal) => ({
    description: goal.description ?? "",
    progress: "",
  }));
};

export const CarePlanForm = ({
  data,
  carePlanView,
  isPp,
  isRegistration = false,
  isReview,
  isLoading,
  onBack,
  onContinue,
}) => {
  const existing = data?.carePlan ?? {};
  // A registration review has no "last review" to carry goals forward from, so
  // the "Goals agreed at my last review" block must never appear (or submit).
  // When the block is hidden (flag off) we also skip the carry-forward: the BE
  // requires progress on at least one carried goal, and with the fields hidden
  // there is no way to record it — carrying them would make submit impossible.
  const previousGoalsDefault =
    isRegistration || !SHOW_ALL_CAREPLAN_FIELDS
      ? []
      : buildPreviousGoals(carePlanView, existing.previousGoals);
  // Read-only on the Care Plan: the value carries forward from the first review
  // step ("Other Major Diagnoses"), shown as a list and submitted as joined text.
  const otherMajorDiagnosesList = composeOtherMajorDiagnosesList(
    data?.otherMajorDiagnoses
  );
  // PP replaces "Other Major Diagnoses" with the enrolment risk factors.
  const registrationReasonsList = composeRegistrationReasonsList(
    data?.registrationReasons
  );
  // CDM shows the registered disease list as "Medical Conditions" (auto from the
  // review diagnoses), mirroring PP's enrolment risk-factors list.
  const medicalConditionsList = composeMedicalConditionsList(
    data?.registrationReasons
  );

  // Auto-tick the self-management plan(s) matching the patient's CDM diagnosis
  // (Asthma / COPD / Heart Failure). Option values equal the diagnosis codes.
  const diagnosisSelfManagement = (data?.registrationReasons ?? []).filter(
    (code) =>
      SELF_MANAGEMENT_PLAN_OPTIONS.some((option) => option.value === code)
  );

  const form = useForm({
    resolver: zodResolver(carePlanSchema),
    defaultValues: {
      carerName: existing.carerName ?? "",
      carerConsentToDisclose: existing.carerConsentToDisclose ?? false,
      otherMajorDiagnoses: otherMajorDiagnosesList
        .map((item) => `- ${item}`)
        .join("\n"),
      medicationConcerns: existing.medicationConcerns ?? "",
      medicationIssuesDiscussed: existing.medicationIssuesDiscussed ?? "",
      healthConcerns: existing.healthConcerns ?? "",
      healthImprovements: existing.healthImprovements ?? "",
      thingsThatMatter: existing.thingsThatMatter ?? "",
      healthIssuesDiscussed: existing.healthIssuesDiscussed ?? "",
      conditionManagement: toConditionManagementMap(
        existing.conditionManagement
      ),
      previousGoals: previousGoalsDefault,
      currentGoals: existing.currentGoals?.length
        ? existing.currentGoals.map((goal) => ({
            description: goal.description ?? "",
            plan: goal.plan ?? "",
          }))
        : [{ description: "", plan: "" }],
      anticipatoryCarePlan: existing.anticipatoryCarePlan ?? "",
      printSelfManagementAppendix:
        existing.printSelfManagementAppendix ?? diagnosisSelfManagement,
      referrals: existing.referrals?.length
        ? existing.referrals.map((referral) => ({
            service: referral.service ?? "",
            detail: referral.detail ?? "",
          }))
        : [],
      takeHomeMessages: existing.takeHomeMessages?.length
        ? existing.takeHomeMessages.map((message) => ({ value: message }))
        : [],
      // The picker needs ISO; a saved plan can come back as a compact HL7 date.
      // When none is saved yet, seed an editable default from the programme's
      // review gap (CDM 4mo / PP 9mo) so the clinician doesn't work it out by hand.
      nextReviewDate:
        toCarePlanIsoDate(existing.nextReviewDate) ||
        defaultNextReviewDate(isPp),
      sourcesFreeText: existing.sourcesFreeText ?? "",
      agreedWrittenCarePlan: data?.agreedWrittenCarePlan ?? "",
    },
  });

  const previousGoals = useFieldArray({
    control: form.control,
    name: "previousGoals",
  });
  const currentGoals = useFieldArray({
    control: form.control,
    name: "currentGoals",
  });
  const referrals = useFieldArray({ control: form.control, name: "referrals" });
  const takeHomeMessages = useFieldArray({
    control: form.control,
    name: "takeHomeMessages",
  });

  const referralOptions = getReferralOptions(isPp);
  const selfManagementSelection =
    form.watch("printSelfManagementAppendix") ?? [];

  const toggleSelfManagement = (value) => {
    const current = form.getValues("printSelfManagementAppendix") ?? [];
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    form.setValue("printSelfManagementAppendix", next, {
      shouldValidate: true,
    });
  };

  const handleFormSubmit = (values) => {
    onContinue({
      carePlan: buildPayload(values),
      agreedWrittenCarePlan: values.agreedWrittenCarePlan,
    });
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("CarePlanForm"))}
      isReview={isReview}
      isLastStep
      isLoading={isLoading}
    >
      <FormProvider {...form}>
        <form
          className={style.form}
          onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("CarePlanForm"))}
        >
          {!!isPp && (
            <section className={style.section}>
              <h3>
                Risk factors &amp; reason for enrolment in Prevention Programme
              </h3>
              {registrationReasonsList.length ? (
                <ul className={style.diagnosesList}>
                  {registrationReasonsList.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className={style.autoEmpty}>
                  No risk factors recorded for enrolment.
                </p>
              )}
            </section>
          )}

          <section className={style.section}>
            <h3>Care Plan Details</h3>
            <div className={style.grid3}>
              {SHOW_ALL_CAREPLAN_FIELDS && (
                <div className={style.stackedField}>
                  <FormField
                    name="carerName"
                    label="Name of Carer / Family member (if applicable)"
                  >
                    <input
                      className={style.input}
                      {...form.register("carerName")}
                      disabled={isReview}
                    />
                  </FormField>
                  <FormField name="carerConsentToDisclose">
                    <label className={style.checkboxRow}>
                      <Checkbox
                        checked={!!form.watch("carerConsentToDisclose")}
                        onChange={(e) =>
                          form.setValue(
                            "carerConsentToDisclose",
                            e.target.checked,
                            { shouldValidate: true }
                          )
                        }
                        disabled={isReview}
                      />
                      Consent to disclose care plan to carer / family member
                    </label>
                  </FormField>
                </div>
              )}
              <FormField
                name="agreedWrittenCarePlan"
                label="Agreed Written Care Plan"
              >
                <Select
                  options={AGREED_WRITTEN_CARE_PLAN_OPTIONS}
                  placeholder="Select"
                  value={form.watch("agreedWrittenCarePlan")}
                  onChange={(value) =>
                    form.setValue("agreedWrittenCarePlan", value, {
                      shouldValidate: true,
                    })
                  }
                  disabled={isReview}
                  required
                />
              </FormField>
              <FormField name="nextReviewDate" label="Next review date">
                <DatePickerField
                  value={form.watch("nextReviewDate")}
                  onChange={(isoDate) =>
                    form.setValue("nextReviewDate", isoDate ?? "", {
                      shouldValidate: true,
                    })
                  }
                  disabled={isReview}
                />
              </FormField>
            </div>
          </section>

          {!isPp && (
            <section className={style.section}>
              <h3>Medical Conditions</h3>
              {medicalConditionsList.length ? (
                <ul className={style.diagnosesList}>
                  {medicalConditionsList.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className={style.autoEmpty}>
                  No medical conditions recorded.
                </p>
              )}
            </section>
          )}

          {!isPp && SHOW_ALL_CAREPLAN_FIELDS && (
            <section className={style.section}>
              <h3>Other Major Diagnoses</h3>
              {otherMajorDiagnosesList.length ? (
                <ul className={style.diagnosesList}>
                  {otherMajorDiagnosesList.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className={style.autoEmpty}>
                  No other major diagnoses recorded.
                </p>
              )}
            </section>
          )}

          <CarePlanAutoBlocks data={data} />

          <section className={style.section}>
            <h3>Medications</h3>
            <div
              className={
                SHOW_ALL_CAREPLAN_FIELDS ? style.grid2 : style.gridSingle
              }
            >
              {SHOW_ALL_CAREPLAN_FIELDS && (
                <FormField
                  name="medicationConcerns"
                  label={
                    <span className={style.labelWithInfo}>
                      Patient&apos;s medication questions / concerns
                      <InfoTooltip text={CARE_PLAN_INFO.medicationConcerns} />
                    </span>
                  }
                >
                  <Textarea
                    rows={3}
                    placeholder="e.g. Nauseous when I take Drug 2"
                    {...form.register("medicationConcerns")}
                    disabled={isReview}
                  />
                </FormField>
              )}
              <FormField
                name="medicationIssuesDiscussed"
                label={
                  <span className={style.labelWithInfo}>
                    Medication issues discussed
                    <InfoTooltip text={CARE_PLAN_INFO.medicationIssuesDiscussed} />
                  </span>
                }
              >
                <Textarea
                  rows={3}
                  placeholder="e.g. Take Drug 2 after breakfast"
                  {...form.register("medicationIssuesDiscussed")}
                  disabled={isReview}
                />
              </FormField>
            </div>
          </section>

          <section className={style.section}>
            <h3>Health &amp; Wellbeing</h3>
            <div
              className={
                SHOW_ALL_CAREPLAN_FIELDS ? style.grid2 : style.gridSingle
              }
            >
              {SHOW_ALL_CAREPLAN_FIELDS && (
                <>
                  <FormField
                    name="healthConcerns"
                    label={
                      <span className={style.labelWithInfo}>
                        Patient&apos;s health concerns
                        <InfoTooltip text={CARE_PLAN_INFO.healthConcerns} />
                      </span>
                    }
                  >
                    <Textarea
                      rows={3}
                      {...form.register("healthConcerns")}
                      disabled={isReview}
                    />
                  </FormField>
                  <FormField
                    name="healthImprovements"
                    label={
                      <span className={style.labelWithInfo}>
                        Areas of health that are good or have improved
                        <InfoTooltip text={CARE_PLAN_INFO.healthImprovements} />
                      </span>
                    }
                  >
                    <Textarea
                      rows={3}
                      {...form.register("healthImprovements")}
                      disabled={isReview}
                    />
                  </FormField>
                  <FormField
                    name="thingsThatMatter"
                    label={
                      <span className={style.labelWithInfo}>
                        Other things that matter to the patient
                        <InfoTooltip text={CARE_PLAN_INFO.thingsThatMatter} />
                      </span>
                    }
                  >
                    <Textarea
                      rows={3}
                      {...form.register("thingsThatMatter")}
                      disabled={isReview}
                    />
                  </FormField>
                </>
              )}
              <FormField
                name="healthIssuesDiscussed"
                label={
                  <span className={style.labelWithInfo}>
                    Health issues discussed today
                    <InfoTooltip text={CARE_PLAN_INFO.healthIssuesDiscussed} />
                  </span>
                }
              >
                <Textarea
                  rows={3}
                  {...form.register("healthIssuesDiscussed")}
                  disabled={isReview}
                />
              </FormField>
            </div>
          </section>

          {SHOW_ALL_CAREPLAN_FIELDS && (
            <section className={style.section}>
              <h3>How the patient manages their condition</h3>
              <div className={style.grid3}>
                {MANAGEMENT_TOPICS.map(({ topic, label }) => (
                  <FormField
                    key={topic}
                    name={`conditionManagement.${topic}`}
                    label={label}
                  >
                    <Textarea
                      rows={2}
                      {...form.register(`conditionManagement.${topic}`)}
                      disabled={isReview}
                    />
                  </FormField>
                ))}
              </div>
            </section>
          )}

          <section className={style.section}>
            {SHOW_ALL_CAREPLAN_FIELDS && previousGoals.fields.length > 0 && (
              <div className={style.subBlock}>
                <h3 className={style.headingWithInfo}>
                  Goals agreed at my last review (1 at least mandatory)
                  <InfoTooltip text={CARE_PLAN_INFO.previousGoals} />
                </h3>
                {previousGoals.fields.map((field, index) => (
                  <div key={field.id} className={style.grid2}>
                    <FormField
                      name={`previousGoals.${index}.description`}
                      label="Goal"
                    >
                      <input
                        className={style.input}
                        {...form.register(`previousGoals.${index}.description`)}
                        disabled={isReview}
                      />
                    </FormField>
                    <FormField
                      name={`previousGoals.${index}.progress`}
                      label="Progress"
                    >
                      <input
                        className={style.input}
                        {...form.register(`previousGoals.${index}.progress`)}
                        disabled={isReview}
                      />
                    </FormField>
                  </div>
                ))}
              </div>
            )}

            <div className={style.subBlock}>
              <h3 className={style.headingWithInfo}>
                Goals agreed at this review (1 at least mandatory)
                <InfoTooltip text={CARE_PLAN_INFO.currentGoals} />
              </h3>
              {currentGoals.fields.map((field, index) => (
                <div
                  key={field.id}
                  className={`${style.goalRow} ${
                    !isReview && currentGoals.fields.length > 1
                      ? ""
                      : style.goalRowNoDelete
                  }`}
                >
                  <FormField
                    name={`currentGoals.${index}.description`}
                    label="Goal"
                  >
                    <input
                      className={style.input}
                      {...form.register(`currentGoals.${index}.description`)}
                      disabled={isReview}
                    />
                  </FormField>
                  <FormField name={`currentGoals.${index}.plan`} label="Plan">
                    <input
                      className={style.input}
                      {...form.register(`currentGoals.${index}.plan`)}
                      disabled={isReview}
                    />
                  </FormField>
                  {!isReview && currentGoals.fields.length > 1 && (
                    <button
                      type="button"
                      className={style.removeButton}
                      aria-label="Remove goal"
                      onClick={() => currentGoals.remove(index)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              {!isReview && (
                <div className={style.addRow}>
                  <Button
                    variant="primary"
                    size="medium"
                    type="button"
                    onClick={() =>
                      currentGoals.append({ description: "", plan: "" })
                    }
                  >
                    <Plus size={16} /> Add goal
                  </Button>
                </div>
              )}
            </div>
          </section>

          {!isPp && (
            <section className={style.section}>
              <h3>Anticipatory Care Plan</h3>
              <FormField
                name="anticipatoryCarePlan"
                label={
                  <span className={style.labelWithInfo}>
                    Anticipatory care plan discussed with patient today
                    <InfoTooltip text={CARE_PLAN_INFO.anticipatoryCarePlan} />
                  </span>
                }
              >
                <Textarea
                  rows={3}
                  {...form.register("anticipatoryCarePlan")}
                  disabled={isReview}
                />
              </FormField>
              <p className={style.subheading}>
                Print relevant self-management plan as Appendix
              </p>
              <div className={style.checkboxGroup}>
                {SELF_MANAGEMENT_PLAN_OPTIONS.map((option) => (
                  <label key={option.value} className={style.checkboxRow}>
                    <Checkbox
                      checked={selfManagementSelection.includes(option.value)}
                      onChange={() => toggleSelfManagement(option.value)}
                      disabled={isReview}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </section>
          )}

          {SHOW_ALL_CAREPLAN_FIELDS && (
          <section className={style.section}>
            <h3>Health Services you have been referred to in last 6 months</h3>
            {referrals.fields.length === 0 && (
              <div className={style.emptyCard}>
                No referrals added. Use “Add referral” to record a service the
                patient was referred to.
              </div>
            )}
            {referrals.fields.map((field, index) => (
              <div
                key={field.id}
                className={`${style.goalRow} ${
                  isReview ? style.goalRowNoDelete : ""
                }`}
              >
                <FormField name={`referrals.${index}.service`} label="Service">
                  <Select
                    options={referralOptions}
                    placeholder="Select"
                    value={form.watch(`referrals.${index}.service`)}
                    onChange={(value) =>
                      form.setValue(`referrals.${index}.service`, value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isReview}
                  />
                </FormField>
                <FormField name={`referrals.${index}.detail`} label="Detail">
                  <input
                    className={style.input}
                    {...form.register(`referrals.${index}.detail`)}
                    disabled={isReview}
                  />
                </FormField>
                {!isReview && (
                  <button
                    type="button"
                    className={style.removeButton}
                    aria-label="Remove referral"
                    onClick={() => referrals.remove(index)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
            {!isReview && (
              <div className={style.addRow}>
                <Button
                  variant="primary"
                  size="medium"
                  type="button"
                  onClick={() => referrals.append({ service: "", detail: "" })}
                >
                  <Plus size={16} /> Add referral
                </Button>
              </div>
            )}
          </section>
          )}

          <section className={style.section}>
            <h3>
              Sources of information to help manage your long term condition
            </h3>
            {!!carePlanView?.infoLinks?.length && (
              <div className={style.summaryBlock}>
                {carePlanView.infoLinks.map((link, index) => (
                  <div key={index} className={style.linkRow}>
                    <span className={style.linkLabel}>{link.label}</span>
                    <span className={style.linkDash}>–</span>
                    <a
                      className={style.link}
                      href={ensureAbsoluteUrl(link.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.url}
                    </a>
                    {isDiabetesInfoLink(link.url) && (
                      <InfoTooltip text={CARE_PLAN_INFO.diabetesLink} />
                    )}
                  </div>
                ))}
              </div>
            )}
            <FormField
              name="sourcesFreeText"
              label="Additional sources / notes"
            >
              <Textarea
                rows={3}
                {...form.register("sourcesFreeText")}
                disabled={isReview}
              />
            </FormField>
          </section>

          {SHOW_ALL_CAREPLAN_FIELDS && (
          <section className={style.section}>
            <h3>Remember to (take-home messages)</h3>
            {takeHomeMessages.fields.length === 0 && (
              <div className={style.emptyCard}>
                No take-home messages added. Use “Add message” to add up to{" "}
                {MAX_TAKE_HOME_MESSAGES}.
              </div>
            )}
            {takeHomeMessages.fields.map((field, index) => (
              <div
                key={field.id}
                className={style.goalRow}
                style={{
                  gridTemplateColumns: isReview ? "1fr" : "1fr auto",
                  alignItems: "flex-start",
                }}
              >
                <FormField
                  name={`takeHomeMessages.${index}.value`}
                  label="Message"
                >
                  <Textarea
                    rows={2}
                    {...form.register(`takeHomeMessages.${index}.value`)}
                    disabled={isReview}
                  />
                </FormField>
                {!isReview && (
                  <button
                    type="button"
                    className={style.removeButton}
                    style={{ marginTop: "28px" }}
                    aria-label="Remove message"
                    onClick={() => takeHomeMessages.remove(index)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
            {!isReview &&
              takeHomeMessages.fields.length < MAX_TAKE_HOME_MESSAGES && (
                <div className={style.addRow}>
                  <Button
                    variant="primary"
                    size="medium"
                    type="button"
                    onClick={() => takeHomeMessages.append({ value: "" })}
                  >
                    <Plus size={16} /> Add message
                  </Button>
                </div>
              )}
          </section>
          )}
        </form>
      </FormProvider>
    </ItemFormWrapper>
  );
};
